import React, { useState } from 'react';
import { MusicItem } from '../components/MusicItem';
import { PlusIcon, XIcon, TagIcon } from 'lucide-react';
interface HomeProps {
  onExpandPlayer: () => void;
}
export function Home({
  onExpandPlayer
}: HomeProps) {
  // State for quick tag management
  const [activeTaggingTrackId, setActiveTaggingTrackId] = useState<string | null>(null);
  const [newQuickTag, setNewQuickTag] = useState('');
  // Mock recently played tracks
  const [recentTracks, setRecentTracks] = useState([{
    id: '1',
    title: 'Blinding Lights',
    artist: 'The Weeknd',
    album: 'After Hours',
    cover: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YWZ0ZXIlMjBob3Vyc3xlbnwwfHwwfHx8MA%3D%3D',
    tags: ['summer 2025', 'road trip', 'favorite'],
    location: {
      lat: 40.7128,
      lng: -74.006
    }
  }, {
    id: '2',
    title: 'As It Was',
    artist: 'Harry Styles',
    album: "Harry's House",
    cover: 'https://images.unsplash.com/photo-1551986782-d0169b3f8fa7?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8aG91c2V8ZW58MHx8MHx8fDA%3D',
    tags: ['Hannah', 'beach day', 'sunset drive'],
    location: {
      lat: 34.0522,
      lng: -118.2437
    }
  }, {
    id: '3',
    title: 'Bad Habit',
    artist: 'Steve Lacy',
    album: 'Gemini Rights',
    cover: 'https://images.unsplash.com/photo-1491841550275-ad7854e35ca6?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Z2VtaW5pfGVufDB8fDB8fHww',
    tags: ['roadtrip France', 'with Alex', 'rainy day'],
    location: {
      lat: 37.7749,
      lng: -122.4194
    }
  }, {
    id: '4',
    title: 'Shivers',
    artist: 'Ed Sheeran',
    album: '=',
    cover: 'https://images.unsplash.com/photo-1495231916356-a86217efff12?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZXF1YWx8ZW58MHx8MHx8fDA%3D',
    tags: ['morning run', 'Central Park', 'spring 2024'],
    location: {
      lat: 51.5074,
      lng: -0.1278
    }
  }]);
  // Mock recommended playlists
  const recommendedPlaylists = [{
    id: 'p1',
    title: 'Chill Vibes',
    description: 'Relaxing tunes for your evening',
    cover: 'https://images.unsplash.com/photo-1555212697-194d092e3b8f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Y2hpbGx8ZW58MHx8MHx8fDA%3D'
  }, {
    id: 'p2',
    title: 'Workout Mix',
    description: 'High energy tracks to fuel your workout',
    cover: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8d29ya291dHxlbnwwfHwwfHx8MA%3D%3D'
  }, {
    id: 'p3',
    title: 'Focus Flow',
    description: 'Concentrate with these instrumental tracks',
    cover: 'https://images.unsplash.com/photo-1483000805330-4eaf0a0d82da?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Zm9jdXN8ZW58MHx8MHx8fDA%3D'
  }];
  // Personal and contextual tags for quick selection
  const quickTags = ['summer 2025', 'Hannah', 'roadtrip France', 'with Alex', 'beach day', 'morning run', 'sunset drive'];
  // Handle adding a tag to a track
  const handleAddTag = (trackId: string, tag: string) => {
    if (!tag.trim()) return;
    setRecentTracks(tracks => tracks.map(track => {
      if (track.id === trackId) {
        // Only add tag if it doesn't already exist
        if (!track.tags.includes(tag.trim())) {
          return {
            ...track,
            tags: [...track.tags, tag.trim()]
          };
        }
      }
      return track;
    }));
    setNewQuickTag('');
  };
  // Handle removing a tag from a track
  const handleRemoveTag = (trackId: string, tagToRemove: string) => {
    setRecentTracks(tracks => tracks.map(track => {
      if (track.id === trackId) {
        return {
          ...track,
          tags: track.tags.filter(tag => tag !== tagToRemove)
        };
      }
      return track;
    }));
  };
  return <div className="px-4 pt-8 pb-20" data-id="element-199">
      <h1 className="text-2xl font-bold mb-6" data-id="element-200">Good evening</h1>
      {/* Recently played */}
      <section className="mb-8" data-id="element-201">
        <h2 className="text-lg font-semibold mb-4" data-id="element-202">Recently Played</h2>
        <div className="space-y-2" data-id="element-203">
          {recentTracks.map(track => <div key={track.id} className="relative" data-id="element-204">
              <MusicItem {...track} onPlay={onExpandPlayer} onTagClick={() => setActiveTaggingTrackId(track.id === activeTaggingTrackId ? null : track.id)} data-id="element-205" />
              {/* Quick tagging interface */}
              {activeTaggingTrackId === track.id && <div className="mt-2 ml-14 mr-2 p-3.5 bg-zinc-800/95 backdrop-blur-sm rounded-lg border border-zinc-700 shadow-xl" data-id="element-206">
                  {/* Current tags */}
                  <div className="mb-3.5" data-id="element-207">
                    <div className="flex items-center mb-2" data-id="element-208">
                      <TagIcon size={14} className="text-zinc-400 mr-1.5" data-id="element-209" />
                      <p className="text-xs text-zinc-400" data-id="element-210">Current tags</p>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-211">
                      {track.tags.length > 0 ? track.tags.map(tag => <div key={tag} className="px-2.5 py-1 bg-zinc-700 rounded-full text-xs flex items-center" data-id="element-212">
                            <span data-id="element-213">{tag}</span>
                            <button className="ml-1.5 text-zinc-400 hover:text-white" onClick={() => handleRemoveTag(track.id, tag)} data-id="element-214">
                              <XIcon size={10} data-id="element-215" />
                            </button>
                          </div>) : <p className="text-xs text-zinc-500" data-id="element-216">No tags yet</p>}
                    </div>
                  </div>
                  {/* Add new tag */}
                  <div className="mb-3.5" data-id="element-217">
                    <div className="flex" data-id="element-218">
                      <input type="text" value={newQuickTag} onChange={e => setNewQuickTag(e.target.value)} placeholder="Add a personal tag..." className="flex-1 bg-zinc-700 border border-zinc-600 rounded-l-md px-3 py-1.5 text-xs focus:outline-none focus:border-green-500" onKeyDown={e => {
                  if (e.key === 'Enter') handleAddTag(track.id, newQuickTag);
                }} data-id="element-219" />
                      <button onClick={() => handleAddTag(track.id, newQuickTag)} className="bg-green-500 text-black px-2.5 rounded-r-md flex items-center" data-id="element-220">
                        <PlusIcon size={14} data-id="element-221" />
                      </button>
                    </div>
                  </div>
                  {/* Recent tags */}
                  <div data-id="element-222">
                    <p className="text-xs text-zinc-400 mb-2" data-id="element-223">
                      Your recent tags
                    </p>
                    <div className="flex flex-wrap gap-2 mb-1" data-id="element-224">
                      {quickTags.map(tag => <button key={tag} onClick={() => handleAddTag(track.id, tag)} className={`px-2.5 py-1 rounded-full text-xs ${track.tags.includes(tag) ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-zinc-700 text-zinc-300'}`} data-id="element-225">
                          {tag}
                        </button>)}
                    </div>
                  </div>
                  <div className="mt-3.5 flex justify-end" data-id="element-226">
                    <button onClick={() => setActiveTaggingTrackId(null)} className="text-xs text-green-500 font-medium" data-id="element-227">
                      Done
                    </button>
                  </div>
                </div>}
            </div>)}
        </div>
      </section>
      {/* Recommended playlists */}
      <section data-id="element-228">
        <h2 className="text-lg font-semibold mb-4" data-id="element-229">Recommended Playlists</h2>
        <div className="grid grid-cols-2 gap-4" data-id="element-230">
          {recommendedPlaylists.map(playlist => <div key={playlist.id} className="rounded-md overflow-hidden" data-id="element-231">
              <img src={playlist.cover} alt={playlist.title} className="w-full aspect-square object-cover" data-id="element-232" />
              <div className="bg-zinc-800/80 p-2" data-id="element-233">
                <p className="font-medium text-sm" data-id="element-234">{playlist.title}</p>
                <p className="text-xs text-zinc-400 truncate" data-id="element-235">
                  {playlist.description}
                </p>
              </div>
            </div>)}
        </div>
      </section>
    </div>;
}